import { useState, type CSSProperties } from 'react';
import { analyserBlocAdresse } from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useMajAdresse, type AdresseSaisie } from '../api/hooks';
import type { CommandeDetaillee } from '../api/types';
import { useToasts } from './Toasts';

/**
 * L'adresse de livraison, dans le récapitulatif — et sa correction.
 *
 * Elle se corrige tant que la marchandise n'est pas chez le transporteur :
 * enlèvement pas encore demandé chez HIOLLE, ni expédition ni tracking
 * ailleurs. Au-delà, c'est le transporteur qui porte l'adresse, et l'API dit
 * pourquoi dans `adresseVerrouillee`.
 *
 * Affichée telle que l'enlèvement et le mail fournisseur la lisent : champs
 * dédiés d'abord, bloc « détails de livraison » à défaut.
 */
export function AdresseCommande({ commande }: { commande: CommandeDetaillee }) {
  const majAdresse = useMajAdresse();
  const { ajouter } = useToasts();
  const [brouillon, setBrouillon] = useState<AdresseSaisie | null>(null);

  const a = commande.adresse;
  const bloc = analyserBlocAdresse(a.detailsLivraison);
  const ou = (enregistre: string | null, lu: string | null | undefined) =>
    enregistre?.trim() || lu?.trim() || '';
  const affichee: AdresseSaisie = {
    // Le nom du destinataire ne se modifie que dans la demande d'enlèvement :
    // il est renvoyé tel qu'enregistré.
    nom: a.nom ?? '',
    telephone: a.telephone ?? '',
    ligne: ou(a.ligne, bloc?.ligne),
    complement: a.complement?.trim() ?? '',
    codePostal: ou(a.codePostal, bloc?.codePostal),
    ville: ou(a.ville, bloc?.ville),
    pays: ou(a.pays, bloc?.pays),
  };
  const vide = !affichee.ligne && !affichee.codePostal && !affichee.ville && !affichee.pays;

  async function enregistrer() {
    if (!brouillon) return;
    try {
      await majAdresse.mutateAsync({ id: commande.id, adresse: brouillon });
      setBrouillon(null);
    } catch (e) {
      ajouter(e instanceof ErreurApi ? e.message : 'L’adresse n’a pas pu être enregistrée.', 'bad');
    }
  }

  if (brouillon) {
    const champ = (cle: keyof AdresseSaisie, libelle: string, max: number, style?: CSSProperties) => (
      <input
        type="text" value={brouillon[cle]} placeholder={libelle} aria-label={libelle}
        maxLength={max} style={style} autoFocus={cle === 'ligne'}
        onChange={(e) => setBrouillon({ ...brouillon, [cle]: e.target.value })}
      />
    );
    return (
      <form
        style={{ marginTop: 10 }}
        onSubmit={(e) => { e.preventDefault(); void enregistrer(); }}
      >
        <div className="rule">{champ('ligne', 'Rue', 200)}</div>
        <div className="rule">{champ('complement', 'Complément — bâtiment, étage, lieu-dit…', 100)}</div>
        <div className="rule">
          {champ('codePostal', 'Code postal', 20, { flex: '0 0 100px' })}
          {champ('ville', 'Ville', 100)}
          {champ('pays', 'Pays', 60, { flex: '0 0 110px' })}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="submit" className="btn pri sm" disabled={majAdresse.isPending}>
            {majAdresse.isPending ? <><span className="spin" /> …</> : 'Enregistrer'}
          </button>
          <button
            type="button" className="btn sm" disabled={majAdresse.isPending}
            onClick={() => setBrouillon(null)}
          >
            Annuler
          </button>
        </div>
      </form>
    );
  }

  return (
    <div style={{ marginTop: 10, fontSize: 12.5 }}>
      <div style={{ color: 'var(--ink-3)', marginBottom: 2 }}>Adresse de livraison</div>
      {vide
        ? <em style={{ color: 'var(--brick)' }}>aucune adresse</em>
        : (
          <div style={{ fontWeight: 600 }}>
            {affichee.ligne || <em style={{ color: 'var(--brick)', fontWeight: 400 }}>rue manquante</em>}<br />
            {affichee.complement && <>{affichee.complement}<br /></>}
            {[affichee.codePostal, affichee.ville].filter(Boolean).join(' ')
              || <em style={{ color: 'var(--brick)', fontWeight: 400 }}>code postal et ville manquants</em>}
            {affichee.pays && ` · ${affichee.pays}`}
          </div>
        )}
      {commande.adresseVerrouillee
        ? <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>{commande.adresseVerrouillee}</div>
        : (
          <button
            type="button" className="lien-etape" style={{ fontSize: 11.5, marginTop: 4 }}
            onClick={() => setBrouillon(affichee)}
          >
            Modifier
          </button>
        )}
    </div>
  );
}
